/**
 * CalendarModuleV2.tsx
 *
 * Versión 2 del módulo de calendario para la vertical de peluquería.
 * Integra el módulo externo calendar-module-integration.jsx convertido a TypeScript.
 * Vistas: semana, profesional, mes, año, agenda.
 * Conecta con appointment-service y professional-service para datos reales.
 *
 * Flag `g` (GlowPro mode):
 *   true  → estilo suave, italic, redondeado (GlowPro)
 *   false → estilo sharp, uppercase, bold     (BarberPro)
 */

import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft } from 'lucide-react';
import type { Appointment, Professional, HairService } from '../../models';
import {
  getAppointmentsByRange,
  createAppointment,
  updateAppointmentStatus,
} from '../../services/appointment-service';
import {
  getProfessionals,
  getHairServices,
} from '../../services/professional-service';
import type { CreateAppointmentInput } from '../../services/appointment-service';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CalV2Props {
  /** true = GlowPro styling (soft/italic), false = BarberPro (sharp/bold) */
  g: boolean;
  onNavigateInbox?: () => void;
  onViewClient?: (id: string) => void;
  onBack?: () => void;
}

interface CalEntry {
  id: string;
  date: Date;
  startH: number;
  startM: number;
  endH: number;
  endM: number;
  clientName: string;
  clientId?: string;
  phone?: string;
  email?: string;
  proId?: string;
  serviceId?: string;
  price: number;
  status: string;
  origin: string;
  notes?: string;
}

interface ProEntry {
  id: string;
  name: string;
  color: string;
  bg: string;
  initials: string;
}

interface SvcEntry {
  id: string;
  name: string;
  duration: number;
  price: number;
}

// ─── Mappers ─────────────────────────────────────────────────────────────────

function toCalEntry(a: Appointment, svcs: HairService[]): CalEntry {
  const start = new Date(a.startDatetime);
  const end = new Date(a.endDatetime);
  const svc = svcs.find(s => s.id === a.serviceId);
  const statusMap: Record<string, string> = {
    completed: 'done',
    'no-show': 'noshow',
  };
  return {
    id: a.id,
    date: start,
    startH: start.getHours(),
    startM: start.getMinutes(),
    endH: end.getHours(),
    endM: end.getMinutes(),
    clientName: a.clientName,
    clientId: a.contactId,
    phone: a.clientPhone,
    email: a.clientEmail,
    proId: a.professionalId,
    serviceId: a.serviceId,
    price: svc?.price ?? 0,
    status: statusMap[a.status] ?? a.status,
    origin: a.origin,
    notes: a.notes,
  };
}

function toProEntry(p: Professional): ProEntry {
  const initials = p.name
    .split(' ')
    .slice(0, 2)
    .map(n => n[0])
    .join('')
    .toUpperCase();
  const hex = p.colorHex || '#EC4899';
  const alpha = (opacity: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${opacity})`;
  };
  return {
    id: p.id,
    name: p.name,
    color: hex,
    bg: alpha(0.15),
    initials,
  };
}

function toSvcEntry(s: HairService): SvcEntry {
  return { id: s.id, name: s.name, duration: s.durationMinutes, price: s.price };
}

// ─── Utils ───────────────────────────────────────────────────────────────────

const HOURS = Array.from({ length: 14 }, (_, i) => i + 8);
const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const WDAYS  = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];
const WDAYS_S = ['L','M','X','J','V','S','D'];

const STATUSES: Record<string, { label: string; color: string; bg: string }> = {
  pending:   { label: 'Pendiente',  color: 'var(--status-pending)',   bg: 'rgba(212,178,69,.14)'  },
  confirmed: { label: 'Confirmada', color: 'var(--status-confirmed)', bg: 'rgba(77,202,138,.14)'  },
  ongoing:   { label: 'En curso',   color: 'var(--status-ongoing)',   bg: 'rgba(91,156,246,.14)'  },
  done:      { label: 'Completada', color: 'var(--status-done)',      bg: 'rgba(144,154,170,.14)' },
  cancelled: { label: 'Cancelada',  color: 'var(--status-cancelled)', bg: 'rgba(212,80,58,.14)'   },
  noshow:    { label: 'No-show',    color: '#a0508a',                 bg: 'rgba(160,80,138,.14)'  },
  blocked:   { label: 'Bloqueado',  color: 'var(--status-blocked)',   bg: 'rgba(96,100,112,.14)'  },
};

const addDays  = (d: Date, n: number) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const sameDay  = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const fmtTime  = (h: number, m: number) => `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
const fmtDate  = (d: Date) => d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
const apptTop  = (h: number, m: number) => (h - 8) * 60 + (m / 60) * 60;
const apptH    = (sH: number, sM: number, eH: number, eM: number) => Math.max(((eH*60+eM)-(sH*60+sM))/60*60, 24);
const apptDur  = (sH: number, sM: number, eH: number, eM: number) => (eH*60+eM) - (sH*60+sM);

function weekStart(d: Date) {
  const r = new Date(d);
  const dow = r.getDay();
  r.setDate(r.getDate() - ((dow === 0 ? 7 : dow) - 1));
  return r;
}

function monthCells(y: number, m: number): (Date | null)[] {
  const first = new Date(y, m, 1);
  const dow   = first.getDay() === 0 ? 6 : first.getDay() - 1;
  const total = new Date(y, m + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < dow; i++) cells.push(null);
  for (let i = 1; i <= total; i++) cells.push(new Date(y, m, i));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatusBadge({ status, g }: { status: string; g: boolean }) {
  const s = STATUSES[status];
  if (!s) return null;
  return (
    <span style={{
      fontSize: 10, fontFamily: 'var(--font-mono)',
      padding: g ? '2px 7px' : '1px 5px',
      borderRadius: g ? '10px' : '2px',
      background: s.bg, color: s.color, fontWeight: 500,
      textTransform: g ? 'none' as const : 'uppercase' as const,
    }}>{s.label}</span>
  );
}

function WeekView({ weekDays, appts, pros, svcs, onSelect, today }: {
  weekDays: Date[]; appts: CalEntry[]; pros: ProEntry[]; svcs: SvcEntry[];
  onSelect: (a: CalEntry) => void; today: Date;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = 55; }, []);
  const nowTop = apptTop(today.getHours(), today.getMinutes());

  return (
    <div className="week-grid">
      <div className="week-header-row">
        <div style={{ borderRight: '1px solid var(--border)' }} />
        {weekDays.map((d, i) => (
          <div key={i} className={`week-header-cell${sameDay(d, today) ? ' today' : ''}`}>
            <span className="day-num">{d.getDate()}</span>
            {d.toLocaleDateString('es-ES', { weekday: 'short' }).toUpperCase()}
          </div>
        ))}
      </div>
      <div className="week-scroll" ref={ref}>
        <div className="week-time-grid">
          <div>{HOURS.map(h => <div key={h} className="time-label">{String(h).padStart(2,'0')}:00</div>)}</div>
          {weekDays.map((d, di) => {
            const da = appts.filter(a => sameDay(a.date, d));
            return (
              <div key={di} className="day-col">
                {HOURS.map(h => <div key={h} className="hour-slot" />)}
                {sameDay(d, today) && <div className="current-time-line" style={{ top: nowTop + 'px' }} />}
                {da.map(a => {
                  const pro = pros.find(p => p.id === a.proId);
                  const svc = svcs.find(s => s.id === a.serviceId);
                  const originIcon = a.origin === 'form' ? '📋' : a.origin === 'web' ? '🌐' : null;
                  return (
                    <div
                      key={a.id}
                      className={`appt-card sc-${a.status}`}
                      style={{ top: apptTop(a.startH, a.startM) + 'px', height: apptH(a.startH, a.startM, a.endH, a.endM) + 'px' }}
                      onClick={() => onSelect(a)}
                    >
                      <div className="appt-time">
                        {fmtTime(a.startH, a.startM)}
                        {originIcon && <span style={{ marginLeft: 3 }}>{originIcon}</span>}
                      </div>
                      {a.status === 'blocked'
                        ? <div className="appt-name" style={{ opacity: 0.4 }}>🔒 {a.notes || 'Bloqueado'}</div>
                        : <>
                            <div className="appt-name">{a.clientName}</div>
                            <div className="appt-service">{svc?.name}</div>
                            {pro && <div className="appt-pro-badge" style={{ background: pro.bg, color: pro.color }}>{pro.initials}</div>}
                          </>
                      }
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ProView({ date, appts, pros, svcs, onSelect, today }: {
  date: Date; appts: CalEntry[]; pros: ProEntry[]; svcs: SvcEntry[];
  onSelect: (a: CalEntry) => void; today: Date;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = 55; }, []);
  const da = appts.filter(a => sameDay(a.date, date));
  const nowTop = apptTop(today.getHours(), today.getMinutes());

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="pro-header-row" style={{ gridTemplateColumns: `54px repeat(${pros.length},1fr)` }}>
        <div style={{ borderRight: '1px solid var(--border)', padding: 12 }} />
        {pros.map(p => (
          <div key={p.id} className="pro-header-cell">
            <div className="pro-avatar" style={{ background: p.bg, color: p.color }}>{p.initials}</div>
            <div>
              <div className="pro-hname">{p.name}</div>
              <div className="pro-hcount">{da.filter(a => a.proId === p.id).length} citas</div>
            </div>
          </div>
        ))}
      </div>
      <div className="week-scroll" ref={ref}>
        <div className="week-time-grid" style={{ gridTemplateColumns: `54px repeat(${pros.length},1fr)` }}>
          <div>{HOURS.map(h => <div key={h} className="time-label">{String(h).padStart(2,'0')}:00</div>)}</div>
          {pros.map(p => {
            const pa = da.filter(a => a.proId === p.id);
            return (
              <div key={p.id} className="day-col">
                {HOURS.map(h => <div key={h} className="hour-slot" />)}
                {sameDay(date, today) && <div className="current-time-line" style={{ top: nowTop + 'px' }} />}
                {pa.map(a => {
                  const svc = svcs.find(s => s.id === a.serviceId);
                  return (
                    <div
                      key={a.id}
                      className={`appt-card sc-${a.status}`}
                      style={{ top: apptTop(a.startH, a.startM) + 'px', height: apptH(a.startH, a.startM, a.endH, a.endM) + 'px' }}
                      onClick={() => onSelect(a)}
                    >
                      <div className="appt-time">{fmtTime(a.startH, a.startM)} → {fmtTime(a.endH, a.endM)}</div>
                      {a.status === 'blocked'
                        ? <div className="appt-name" style={{ opacity: 0.4 }}>🔒 {a.notes || 'Bloqueado'}</div>
                        : <><div className="appt-name">{a.clientName}</div><div className="appt-service">{svc?.name}</div></>
                      }
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MonthView({ year, month, appts, g, onSelect, onDayClick, today }: {
  year: number; month: number; appts: CalEntry[];
  g: boolean; onSelect: (a: CalEntry) => void;
  onDayClick: (d: Date) => void; today: Date;
}) {
  const cells = monthCells(year, month);
  const MAX = g ? 4 : 3;
  return (
    <div className="month-grid">
      <div className="month-wdays">
        {WDAYS.map(d => <div key={d} className="month-wday">{d}</div>)}
      </div>
      <div className="month-cells">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="month-cell other-month" />;
          const da = appts.filter(a => sameDay(a.date, d)).sort((a, b) => a.startH - b.startH);
          const isToday = sameDay(d, today);
          const shown = da.slice(0, MAX);
          const more  = da.length - MAX;
          return (
            <div key={i} className={`month-cell${isToday ? ' is-today' : ''}`} onClick={() => onDayClick(d)}>
              <div className="month-day-num">{d.getDate()}</div>
              {da.length > 0 && <div className="month-total">{da.length}</div>}
              {shown.map(a => {
                const s = STATUSES[a.status];
                return (
                  <div
                    key={a.id} className="month-pill"
                    style={{ background: s?.bg, color: s?.color, borderLeft: g ? 'none' : `2px solid ${s?.color}` }}
                    onClick={e => { e.stopPropagation(); onSelect(a); }}
                  >
                    {a.status === 'blocked' ? '🔒 Bloqueado' : `${fmtTime(a.startH, a.startM)} ${a.clientName || '—'}`}
                  </div>
                );
              })}
              {more > 0 && (
                <div className="month-more" onClick={e => { e.stopPropagation(); onDayClick(d); }}>+{more} más</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function YearView({ year, appts, g, today, onMonthClick }: {
  year: number; appts: CalEntry[]; g: boolean; today: Date; onMonthClick: (mi: number) => void;
}) {
  const yearAppts = appts.filter(a => a.date.getFullYear() === year);
  const total     = yearAppts.length;
  const revenue   = yearAppts.filter(a => !['blocked','cancelled'].includes(a.status)).reduce((s, a) => s + a.price, 0);

  return (
    <div className="year-grid">
      <div className="year-stat-header">{year} · {total} citas · {revenue.toLocaleString('es-ES')}€ facturado</div>
      <div className="year-months">
        {MONTHS.map((mname, mi) => {
          const ma      = appts.filter(a => a.date.getFullYear() === year && a.date.getMonth() === mi);
          const rev     = ma.filter(a => !['blocked','cancelled'].includes(a.status)).reduce((s,a) => s+a.price, 0);
          const conf    = ma.filter(a => ['confirmed','done'].includes(a.status)).length;
          const cells   = monthCells(year, mi);
          const isCur   = today.getFullYear() === year && today.getMonth() === mi;
          return (
            <div key={mi} className={`year-month-card${isCur ? ' current-month' : ''}`} onClick={() => onMonthClick(mi)}>
              <div className="year-month-name">{mname}</div>
              <div className="mini-wdays">{WDAYS_S.map(d => <div key={d} className="mini-wday">{d}</div>)}</div>
              {Array.from({ length: Math.ceil(cells.length / 7) }, (_, wi) => (
                <div key={wi} className="mini-drow">
                  {cells.slice(wi * 7, wi * 7 + 7).map((d, ci) => {
                    if (!d) return <div key={ci} className="mini-day other" />;
                    const n   = appts.filter(a => sameDay(a.date, d) && a.status !== 'blocked').length;
                    const isT = sameDay(d, today);
                    const cls = isT ? 'is-today' : n === 0 ? '' : n === 1 ? 'has-1' : n === 2 ? 'has-2' : n === 3 ? 'has-3' : 'has-many';
                    return <div key={ci} className={`mini-day${cls ? ' '+cls : ''}`}>{d.getDate()}</div>;
                  })}
                </div>
              ))}
              <div className="year-month-stats">
                <div className="yms-item"><div className="yms-n">{ma.length}</div><div className="yms-l">citas</div></div>
                <div className="yms-item"><div className="yms-n" style={{ fontSize: g ? '14px' : '16px' }}>{rev}€</div><div className="yms-l">factur.</div></div>
                <div className="yms-item"><div className="yms-n" style={{ color: 'var(--status-confirmed)' }}>{conf}</div><div className="yms-l">confirm</div></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AgendaView({ appts, svcs, pros, g, onSelect, today }: {
  appts: CalEntry[]; svcs: SvcEntry[]; pros: ProEntry[]; g: boolean;
  onSelect: (a: CalEntry) => void; today: Date;
}) {
  const sorted = [...appts]
    .filter(a => a.date >= new Date(today.getFullYear(), today.getMonth(), today.getDate()))
    .sort((a, b) => a.date.getTime() - b.date.getTime() || a.startH - b.startH || a.startM - b.startM);
  const grouped: Record<string, CalEntry[]> = {};
  for (const a of sorted) {
    const key = a.date.toDateString();
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(a);
  }
  return (
    <div className="agenda-view">
      {Object.entries(grouped).map(([key, dayAppts]) => {
        const d = new Date(key);
        const label = sameDay(d, today) ? 'Hoy' : d.toLocaleDateString('es-ES', { weekday: 'long' });
        return (
          <div key={key} className="agenda-day-group">
            <div className="agenda-day-header">
              <span className="agenda-day-date">{fmtDate(d)}</span>
              <span className="agenda-day-label">{label.charAt(0).toUpperCase() + label.slice(1)}</span>
              <div className="agenda-day-line" />
              <span className="agenda-day-count">{dayAppts.length} citas</span>
            </div>
            {dayAppts.map(a => {
              const svc = svcs.find(s => s.id === a.serviceId);
              const pro = pros.find(p => p.id === a.proId);
              const dur = apptDur(a.startH, a.startM, a.endH, a.endM);
              const s   = STATUSES[a.status];
              return (
                <div key={a.id} className="agenda-appt" onClick={() => onSelect(a)}
                  style={{ borderLeft: `3px solid ${s?.color || 'var(--border)'}` }}>
                  <div className="agenda-time-col">
                    <div className="agenda-start">{fmtTime(a.startH, a.startM)}</div>
                    <div className="agenda-dur">{dur} min</div>
                  </div>
                  <div className="agenda-vdiv" />
                  <div className="agenda-info">
                    <div className="agenda-client">{a.clientName || '—'}</div>
                    <div className="agenda-meta">
                      {svc && <span className="agenda-svc-chip">{svc.name}</span>}
                      {pro && <span className="agenda-svc-chip" style={{ color: pro.color }}>{pro.name}</span>}
                      <StatusBadge status={a.status} g={g} />
                    </div>
                  </div>
                  {a.price > 0 && <div className="agenda-price">{a.price}€</div>}
                </div>
              );
            })}
          </div>
        );
      })}
      {Object.keys(grouped).length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
          Sin citas próximas
        </div>
      )}
    </div>
  );
}

function DetailPanel({ appt, pros, svcs, g, onClose, onStatusChange, onOpenInbox }: {
  appt: CalEntry | null; pros: ProEntry[]; svcs: SvcEntry[]; g: boolean;
  onClose: () => void; onStatusChange: (id: string, status: string) => void;
  onOpenInbox?: () => void;
}) {
  const r = g ? '20px' : '2px';
  if (!appt) {
    return (
      <div className="detail-panel">
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', gap: 12 }}>
          <div style={{ fontSize: 32 }}>{g ? '🌸' : '📋'}</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>Selecciona una cita</div>
        </div>
      </div>
    );
  }
  const pro = pros.find(p => p.id === appt.proId);
  const svc = svcs.find(s => s.id === appt.serviceId);
  const dur = apptDur(appt.startH, appt.startM, appt.endH, appt.endM);

  return (
    <div className="detail-panel slide-in">
      <div className="detail-panel-header">
        <span className="detail-panel-title">{g ? 'Detalle de cita' : 'DETALLE CITA'}</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16 }}>✕</button>
      </div>
      <div className="detail-panel-body">
        <div className="detail-status-bar">
          {Object.entries(STATUSES).map(([k, s]) => (
            <button key={k} className="detail-status-btn"
              style={{
                background:  appt.status === k ? s.bg : 'transparent',
                color:       appt.status === k ? s.color : 'var(--text-muted)',
                borderColor: appt.status === k ? s.color + '55' : 'var(--border)',
                borderRadius: r,
              }}
              onClick={() => onStatusChange(appt.id, k)}
            >{s.label}</button>
          ))}
        </div>
        <div className="detail-section">
          <div className="detail-section-title">Cliente</div>
          {appt.status !== 'blocked' ? <>
            <div className="detail-row"><span className="detail-label">Nombre</span><span className="detail-value">{appt.clientName || '—'}</span></div>
            {appt.phone && <div className="detail-row"><span className="detail-label">Tel.</span><span className="detail-value mono">{appt.phone}</span></div>}
            {appt.email && <div className="detail-row"><span className="detail-label">Email</span><span className="detail-value mono" style={{ fontSize: 11 }}>{appt.email}</span></div>}
          </> : <div className="detail-row"><span className="detail-value" style={{ color: 'var(--text-muted)' }}>Hueco bloqueado</span></div>}
        </div>
        <div className="detail-section">
          <div className="detail-section-title">Cita</div>
          <div className="detail-row"><span className="detail-label">Fecha</span><span className="detail-value mono">{fmtDate(appt.date)}</span></div>
          <div className="detail-row"><span className="detail-label">Hora</span><span className="detail-value mono">{fmtTime(appt.startH, appt.startM)} → {fmtTime(appt.endH, appt.endM)}</span></div>
          <div className="detail-row"><span className="detail-label">Duración</span><span className="detail-value mono">{dur} min</span></div>
          {svc && <div className="detail-row"><span className="detail-label">Servicio</span><span className="detail-value">{svc.name}</span></div>}
          {pro && (
            <div className="detail-row">
              <span className="detail-label">Profesional</span>
              <span className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: pro.color, display: 'inline-block' }} />
                {pro.name}
              </span>
            </div>
          )}
          {appt.price > 0 && <div className="detail-row"><span className="detail-label">Precio</span><span className="detail-value price">{appt.price}€</span></div>}
        </div>
        <div className="detail-section">
          <div className="detail-section-title">Info</div>
          <div className="detail-row"><span className="detail-label">Origen</span><span className="detail-value" style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{appt.origin}</span></div>
          {appt.notes && <div className="detail-row"><span className="detail-label">Notas</span><span className="detail-value" style={{ fontStyle: 'italic', color: 'var(--text-secondary)' }}>{appt.notes}</span></div>}
        </div>
      </div>
      <div className="detail-actions">
        <button className="detail-action-btn primary">✏ Editar cita</button>
        {appt.status !== 'blocked' && onOpenInbox && (
          <button className="detail-action-btn" onClick={onOpenInbox}>💬 Ver conversación</button>
        )}
        <button className="detail-action-btn">🔔 Recordatorio</button>
        <button className="detail-action-btn danger" onClick={() => onStatusChange(appt.id, 'cancelled')}>✕ Cancelar cita</button>
      </div>
    </div>
  );
}

function NewApptModal({ g, pros, svcs, onClose, onSave }: {
  g: boolean; pros: ProEntry[]; svcs: SvcEntry[];
  onClose: () => void; onSave: (entry: CalEntry) => void;
}) {
  const todayStr = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    clientName: '', phone: '', email: '',
    proId: pros[0]?.id ?? '',
    serviceId: svcs[0]?.id ?? '',
    date: todayStr, startH: '10', startM: '00',
    notes: '', status: 'confirmed', origin: 'manual',
  });
  const sv   = svcs.find(s => s.id === form.serviceId);
  const eT   = parseInt(form.startH) * 60 + parseInt(form.startM) + (sv?.duration || 30);
  const eLbl = `${String(Math.floor(eT/60)).padStart(2,'0')}:${String(eT%60).padStart(2,'0')}`;
  const set  = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  function handleSave() {
    const [y, m, d] = form.date.split('-').map(Number);
    const sH = parseInt(form.startH), sM = parseInt(form.startM);
    const entry: CalEntry = {
      id: 'tmp-' + Date.now(),
      date: new Date(y, m - 1, d),
      startH: sH, startM: sM,
      endH: Math.floor(eT / 60), endM: eT % 60,
      clientName: form.clientName,
      phone: form.phone,
      email: form.email,
      proId: form.proId,
      serviceId: form.serviceId,
      price: sv?.price ?? 0,
      status: form.status,
      origin: form.origin,
      notes: form.notes,
    };
    onSave(entry);
    onClose();
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal fade-in">
        <div className="modal-header">
          <span className="modal-title">{g ? '✦ Nueva Cita' : 'NUEVA CITA'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Cliente</label>
            <input className="form-input" placeholder="Nombre completo" value={form.clientName} onChange={e => set('clientName', e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Teléfono</label>
              <input className="form-input" placeholder="600 000 000" value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" placeholder="opcional" value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Profesional</label>
              <select className="form-input" value={form.proId} onChange={e => set('proId', e.target.value)}>
                {pros.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Servicio</label>
              <select className="form-input" value={form.serviceId} onChange={e => set('serviceId', e.target.value)}>
                {svcs.map(s => <option key={s.id} value={s.id}>{s.name} — {s.price}€</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Fecha</label>
              <input className="form-input" type="date" value={form.date} onChange={e => set('date', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Hora inicio → fin</label>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <select className="form-input" value={form.startH} onChange={e => set('startH', e.target.value)}>
                  {HOURS.map(h => <option key={h} value={h}>{String(h).padStart(2,'0')}</option>)}
                </select>
                <span style={{ color: 'var(--text-muted)' }}>:</span>
                <select className="form-input" value={form.startM} onChange={e => set('startM', e.target.value)}>
                  {['00','15','30','45'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>→ {eLbl}</span>
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Estado</label>
              <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
                {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Origen</label>
              <select className="form-input" value={form.origin} onChange={e => set('origin', e.target.value)}>
                <option value="manual">Manual</option>
                <option value="web">Web</option>
                <option value="form">Formulario web</option>
                <option value="inbox">Buzón</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notas</label>
            <textarea className="form-input" rows={2} placeholder="Preferencias, observaciones..." value={form.notes} onChange={e => set('notes', e.target.value)} style={{ resize: 'none' }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancelar</button>
          <button className="btn-confirm" onClick={handleSave}>Crear cita</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Module ─────────────────────────────────────────────────────────────

export function CalendarModuleV2({ g, onNavigateInbox, onBack }: CalV2Props) {
  const TODAY = new Date();

  const [view,      setView]      = useState<'week'|'pro'|'month'|'year'|'agenda'>('week');
  const [base,      setBase]      = useState(TODAY);
  const [appts,     setAppts]     = useState<CalEntry[]>([]);
  const [pros,      setPros]      = useState<ProEntry[]>([]);
  const [svcs,      setSvcs]      = useState<SvcEntry[]>([]);
  const [sel,       setSel]       = useState<CalEntry | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [filterPro, setFilterPro] = useState<string | null>(null);
  const [filterSt,  setFilterSt]  = useState<string | null>(null);
  const [,   setLoading]   = useState(true);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart(base), i));

  useEffect(() => {
    Promise.all([getProfessionals(), getHairServices()])
      .then(([p, s]) => { setPros(p.map(toProEntry)); setSvcs(s.map(toSvcEntry)); });
  }, []);

  useEffect(() => {
    const from = weekStart(new Date(base.getFullYear(), base.getMonth(), 1)).getTime();
    const to   = addDays(new Date(base.getFullYear(), base.getMonth() + 2, 0), 7).getTime();
    setLoading(true);
    getAppointmentsByRange(from, to)
      .then(raw => setAppts(prev => {
        const filtered = prev.filter(a => a.date.getTime() < from || a.date.getTime() > to);
        return [...filtered, ...raw.map(a => toCalEntry(a, svcs.map(s => ({ id: s.id, name: s.name, durationMinutes: s.duration, bufferMinutes: 0, price: s.price, active: true, sortOrder: 0, createdAt: 0, updatedAt: 0 } as HairService))))]
      }))
      .finally(() => setLoading(false));
  }, [base]);

  const filtered = appts.filter(a =>
    (!filterPro || a.proId === filterPro) &&
    (!filterSt  || a.status === filterSt)
  );

  function navigate(dir: number) {
    const n = new Date(base);
    if      (view === 'week')  n.setDate(base.getDate() + dir * 7);
    else if (view === 'pro')   n.setDate(base.getDate() + dir);
    else if (view === 'month') n.setMonth(base.getMonth() + dir);
    else                       n.setFullYear(base.getFullYear() + dir);
    setBase(n);
  }

  function changeStatus(id: string, status: string) {
    setAppts(p => p.map(a => a.id === id ? { ...a, status } : a));
    setSel(p => p?.id === id ? { ...p, status } : p);
    updateAppointmentStatus(id, status as any).catch(console.error);
  }

  function addAppt(entry: CalEntry) {
    setAppts(p => [...p, entry]);
    const svcModel = svcs.find(s => s.id === entry.serviceId);
    if (!svcModel) return;
    const input: CreateAppointmentInput = {
      clientName: entry.clientName,
      clientPhone: entry.phone || '',
      clientEmail: entry.email || '',
      serviceId: entry.serviceId || '',
      serviceName: svcModel.name,
      durationMinutes: svcModel.duration,
      bufferMinutes: 0,
      professionalId: entry.proId || '',
      professionalName: pros.find(p => p.id === entry.proId)?.name ?? '',
      startDatetime: entry.date.setHours(entry.startH, entry.startM, 0, 0),
      status: entry.status as any,
      origin: entry.origin as any,
      notes: entry.notes,
    };
    createAppointment(input).catch(console.error);
  }

  const navLabel = view === 'week'
    ? `${weekDays[0].toLocaleDateString('es-ES',{day:'numeric',month:'short'})} — ${weekDays[6].toLocaleDateString('es-ES',{day:'numeric',month:'short',year:'numeric'})}`
    : view === 'pro' ? fmtDate(base)
    : view === 'month' ? `${MONTHS[base.getMonth()]} ${base.getFullYear()}`
    : `${base.getFullYear()}`;

  const tda     = appts.filter(a => sameDay(a.date, TODAY));
  const nConf   = tda.filter(a => ['confirmed','ongoing'].includes(a.status)).length;
  const nPend   = tda.filter(a => a.status === 'pending').length;
  const nWeb    = tda.filter(a => ['web','form'].includes(a.origin)).length;
  const revenue = tda.filter(a => !['blocked','cancelled'].includes(a.status)).reduce((s,a) => s+a.price, 0);

  const VIEWS: [string, string][] = [
    ['week',   g ? 'Semana'       : 'SEMANA'],
    ['pro',    g ? 'Profesional'  : 'PROFESIONAL'],
    ['month',  g ? 'Mes'          : 'MES'],
    ['year',   g ? 'Año'          : 'AÑO'],
    ['agenda', g ? 'Agenda'       : 'AGENDA'],
  ];

  return (
    <div className="calendar-module">
      <div className="cal-header">
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-1 px-3 py-1.5 bg-pink-100/50 hover:bg-pink-200 text-pink-700 rounded-full transition-all border border-pink-200 font-bold text-xs"
            style={{ marginRight: '6px' }}
          >
            <ChevronLeft size={16} /> Volver
          </button>
        )}
        <span className="page-title" style={{ fontFamily: 'var(--font-display)', fontSize: g ? 18 : 16, fontWeight: g ? 400 : 800, fontStyle: g ? 'italic' : 'normal', textTransform: g ? 'none' : 'uppercase' }}>
          {g ? 'Calendario' : 'CALENDARIO'}
        </span>
        <div className="cal-date-nav">
          <button style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-muted)', cursor: 'pointer', padding: '2px 8px' }} onClick={() => navigate(-1)}>‹</button>
          <button className="btn-today" onClick={() => setBase(TODAY)}>HOY</button>
          <span className="cal-current-date">{navLabel}</span>
          <button style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-muted)', cursor: 'pointer', padding: '2px 8px' }} onClick={() => navigate(1)}>›</button>
        </div>
        <div className="cal-qs-sep" />
        {([
          [nConf, 'var(--status-confirmed)', 'confirm'],
          [nPend, 'var(--status-pending)',   'pend'],
          [nWeb,  'var(--status-ongoing)',   'web'],
          [revenue + '€', 'var(--accent)',   'hoy'],
        ] as [string|number, string, string][]).map(([v, c, l]) => (
          <div key={l} className="cal-qs">
            <span className="cal-qs-n" style={{ color: c }}>{v}</span>
            <span className="cal-qs-l">{l}</span>
          </div>
        ))}
        <div className="view-tabs">
          {VIEWS.map(([v, l]) => (
            <div key={v} className={`view-tab${view === v ? ' active' : ''}`} onClick={() => setView(v as any)}>{l}</div>
          ))}
        </div>
        <button
          style={{ marginLeft: 8, padding: '6px 14px', background: 'var(--accent)', border: 'none', borderRadius: g ? '20px' : '3px', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
          onClick={() => setShowModal(true)}
        >+ {g ? 'Nueva cita' : 'NUEVA CITA'}</button>
      </div>

      <div className="cal-toolbar">
        {pros.map(p => (
          <div key={p.id} className={`filter-chip${filterPro === p.id ? ' active' : ''}`} onClick={() => setFilterPro(filterPro === p.id ? null : p.id)}>
            <span className="dot" style={{ background: p.color }} />{p.name}
          </div>
        ))}
        <div style={{ width: 1, height: 16, background: 'var(--border)', margin: '0 2px' }} />
        {(['confirmed','pending','ongoing','blocked'] as const).map(s => (
          <div key={s} className={`filter-chip${filterSt === s ? ' active' : ''}`} onClick={() => setFilterSt(filterSt === s ? null : s)}>
            <span className="dot" style={{ background: STATUSES[s].color }} />{STATUSES[s].label}
          </div>
        ))}
        <div style={{ marginLeft: 'auto', position: 'relative' }}>
          <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 12 }}>⌕</span>
          <input className="form-input" placeholder="Buscar cliente..." style={{ paddingLeft: 28, width: 190, borderRadius: g ? '20px' : '3px' }} />
        </div>
      </div>

      <div className="cal-body">
        {view === 'week'   && <WeekView weekDays={weekDays} appts={filtered} pros={pros} svcs={svcs} onSelect={setSel} today={TODAY} />}
        {view === 'pro'    && <ProView date={base} appts={filtered} pros={pros} svcs={svcs} onSelect={setSel} today={TODAY} />}
        {view === 'month'  && <MonthView year={base.getFullYear()} month={base.getMonth()} appts={filtered} g={g} today={TODAY} onSelect={setSel} onDayClick={d => { setSel(null); setBase(d); setView('week'); }} />}
        {view === 'year'   && <YearView year={base.getFullYear()} appts={filtered} g={g} today={TODAY} onMonthClick={mi => { setBase(new Date(base.getFullYear(), mi, 1)); setView('month'); }} />}
        {view === 'agenda' && <AgendaView appts={filtered} svcs={svcs} pros={pros} g={g} onSelect={setSel} today={TODAY} />}

        <DetailPanel appt={sel} pros={pros} svcs={svcs} g={g} onClose={() => setSel(null)} onStatusChange={changeStatus} onOpenInbox={onNavigateInbox} />
      </div>

      {showModal && pros.length > 0 && svcs.length > 0 && (
        <NewApptModal g={g} pros={pros} svcs={svcs} onClose={() => setShowModal(false)} onSave={addAppt} />
      )}
    </div>
  );
}

export default CalendarModuleV2;
